import { CplatApiResponse } from '@/types/api';
import { OrderList } from '@/types/order-list';
import { queryOptions } from '@tanstack/react-query';
import { CPLAT_API_ROUTES, v3BaseURL } from '.';
import { handleUnauthorizedStatus } from './auth-error';

export type OrderStatusTab = 'NewOrder' | 'ShippingReady' | 'Shipping' | 'ShippingCompleted';
export type OrderChangeStatusTab = 'CancelRequest' | 'ReturnRequest' | 'ExchangeRequest';

export type OrderListQueryParams = {
  cplatToken: string;
  marketId?: number;
  storeId?: string;
  dateFrom: string;
  dateTo: string;
  isOrderFurtherActionEnable?: boolean;
  skip: number;
  take: number;
  statusTab?: OrderStatusTab;
  sortInfo?: {
    sortColumn: string;
    sortDirection: string;
  };
  searchField?: string;
  searchKeyword?: string;
};

export type OrderChangeListQueryParams = Omit<OrderListQueryParams, 'statusTab'> & {
  statusTab?: OrderChangeStatusTab;
};

export const orderQueries = {
  all: () => ['orderQueries'],
  getOrderList: (params: OrderListQueryParams) =>
    queryOptions({
      queryKey: [...orderQueries.all(), 'getOrderList', params] as readonly unknown[],
      queryFn: () => fetchOrderList(params),
      enabled: !!params.cplatToken,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
    }),
  getOrderChangeList: (params: OrderChangeListQueryParams) =>
    queryOptions({
      queryKey: [...orderQueries.all(), 'getOrderChangeList', params] as readonly unknown[],
      queryFn: () => fetchOrderChangeList(params),
      enabled: !!params.cplatToken,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
    }),
};

export const fetchOrderList = async (params: OrderListQueryParams) => {
  const res = await fetch(`${v3BaseURL}${CPLAT_API_ROUTES.getOrderList}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (handleUnauthorizedStatus(res.status)) {
    throw new Error('Unauthorized');
  }

  const json: CplatApiResponse<OrderList[]> = await res.json();
  return json;
};

export const fetchOrderChangeList = async (params: OrderChangeListQueryParams) => {
  const res = await fetch(`${v3BaseURL}${CPLAT_API_ROUTES.getOrderChangeList}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (handleUnauthorizedStatus(res.status)) {
    throw new Error('Unauthorized');
  }

  const json: CplatApiResponse<OrderList[]> = await res.json();
  return json;
};
